import { useState, useEffect, useMemo } from 'react';
import { attendanceService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import PoderUploadBox from './PoderUploadBox';
import styles from './RepresentaAModal.module.css';
import { nombreDe, etiquetaUnidad, coefDe, poderesPorUnidad, esDelRepresentante, agruparPorPropietario } from './poderes';

// "¿A quiénes representa QAB?": se abre desde la fila del REPRESENTANTE (con
// sus datos ya puestos) y se marcan los propietarios que le dieron poder, con
// un documento firmado por cada uno. Si un propietario tiene varias unidades,
// se eligen cuáles entrega (todas marcadas por defecto). Registra un poder por
// propietario (POST /attendances/representative/:id en la fila de la primera
// unidad elegida). El tope del 20% se muestra en vivo sumando también los
// poderes que esta persona ya tenía.
export default function RepresentaAModal({ repItem, items, assemblyId, onClose, onSaved }) {
  const [info,     setInfo]     = useState(null);
  const [infoErr,  setInfoErr]  = useState('');
  const [search,   setSearch]   = useState('');
  const [sel,      setSel]      = useState({});   // ownerId -> { units: [unit_id], file }
  const [saving,   setSaving]   = useState(false);
  const [results,  setResults]  = useState({});   // ownerId -> { ok, message }
  const [error,    setError]    = useState('');

  useEffect(() => {
    attendanceService.powerInfo(assemblyId)
      .then(r => setInfo(r.data))
      .catch(e => setInfoErr(e.response?.data?.message || 'No se pudo cargar la información de poderes.'));
  }, [assemblyId]);

  const repOwner = repItem.owner;
  const repDoc   = repOwner?.numero_identificacion;
  const repName  = nombreDe(repOwner);

  const porUnidad = useMemo(() => poderesPorUnidad(items), [items]);
  const candidatos = useMemo(() => agruparPorPropietario(items)
    .filter(g => g.owner.id !== repOwner?.id)
    .map(g => {
      const filas = g.rows.map(r => {
        const poder = porUnidad.get(Number(r.unit_id)) || null;
        return { row: r, poder, deEsteRep: esDelRepresentante(poder, repDoc) };
      });
      return { ...g, filas, libres: filas.filter(f => !f.poder).map(f => f.row) };
    }), [items, porUnidad, repOwner, repDoc]);

  const q = search.trim().toLowerCase();
  const visibles = q
    ? candidatos.filter(g => nombreDe(g.owner).toLowerCase().includes(q)
        || String(g.owner.numero_identificacion).includes(q)
        || g.rows.some(r => etiquetaUnidad(r).toLowerCase().includes(q)))
    : candidatos;

  const filasYaRep = candidatos.flatMap(g => g.filas.filter(f => f.deEsteRep).map(f => f.row));
  const elegidos   = candidatos.filter(g => sel[g.owner.id]?.units?.length);
  const filasElegidas = elegidos.flatMap(g => g.rows.filter(r => sel[g.owner.id].units.includes(r.unit_id)));
  const coefTotal  = info?.coef_total || 0;
  const maxPct     = info?.max_pct || 20;
  const coefAcum   = coefDe(filasYaRep) + coefDe(filasElegidas);
  const pct        = coefTotal > 0 ? (coefAcum / coefTotal) * 100 : 0;
  const excede     = coefTotal > 0 && pct > maxPct + 1e-9;
  const cuentaActiva = info ? !!info.accounts?.[repOwner?.id] : null;
  const finalizada = info?.assembly_status === 'finalizada';
  const faltanDocs = elegidos.filter(g => !sel[g.owner.id]?.file);

  const toggleOwner = (g) => setSel(s => {
    const n = { ...s };
    if (n[g.owner.id]) delete n[g.owner.id];
    else n[g.owner.id] = { units: g.libres.map(r => r.unit_id), file: null };
    return n;
  });
  const toggleUnit = (g, unitId) => setSel(s => {
    const cur = s[g.owner.id] || { units: [], file: null };
    const units = cur.units.includes(unitId) ? cur.units.filter(u => u !== unitId) : [...cur.units, unitId];
    return { ...s, [g.owner.id]: { ...cur, units } };
  });
  const setFile = (g, f) => setSel(s => ({ ...s, [g.owner.id]: { ...s[g.owner.id], file: f } }));

  const numeros = (g) => g.rows.filter(r => sel[g.owner.id]?.units?.includes(r.unit_id)).map(etiquetaUnidad).join(', ');

  const guardar = async () => {
    setError('');
    if (!elegidos.length) return setError('Marca al menos un propietario representado.');
    if (faltanDocs.length) return setError(`Falta el poder firmado de: ${faltanDocs.map(g => nombreDe(g.owner)).join(', ')}.`);
    if (excede) return setError(`Supera el tope del ${maxPct}% del coeficiente del conjunto.`);
    setSaving(true);
    const res = {};
    for (const g of elegidos) {
      const units = sel[g.owner.id].units;
      const filaPoder = g.rows.find(r => units.includes(r.unit_id));
      const fd = new FormData();
      fd.append('rep_nombre',           repName);
      fd.append('rep_numero_documento', repDoc);
      fd.append('rep_unidades',         JSON.stringify(units));
      fd.append('rep_es_externo',       'false');
      fd.append('documento',            sel[g.owner.id].file);
      try {
        const r = await attendanceService.registerRepresentative(filaPoder.id, fd);
        res[g.owner.id] = { ok: true, message: 'Registrado', aviso: r.data?.aviso || null };
      } catch (e) {
        res[g.owner.id] = { ok: false, message: e.response?.data?.message || 'Error al registrar' };
      }
    }
    setResults(res);
    setSaving(false);
    const fallidos = elegidos.filter(g => !res[g.owner.id]?.ok);
    if (!fallidos.length) {
      const avisos = elegidos.map(g => res[g.owner.id]?.aviso).filter(Boolean);
      onSaved(`${repName} ahora representa a ${elegidos.length} propietario${elegidos.length === 1 ? '' : 's'} más.`, false, avisos.join(' ') || null);
    } else {
      // Los que sí quedaron ya se ven en la tabla; aquí siguen los que no.
      setSel(s => Object.fromEntries(Object.entries(s).filter(([id]) => !res[id]?.ok)));
      // Resumen FIJO arriba con el motivo real de cada fallo: el detalle por propietario está dentro de su tarjeta, que puede quedar
      // fuera de la vista porque «Registrar» está en el pie fijo.
      setError(fallidos.map(g => `${nombreDe(g.owner)}: ${res[g.owner.id].message}`).join(' · '));
      onSaved(null, true);
    }
  };

  const bloqueado = !info || !cuentaActiva || finalizada;
  const botonInactivo = bloqueado || !elegidos.length || excede;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.caja}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>
              <i className={`fas fa-user-friends ${styles.iconoTitulo}`} />
              ¿A quiénes representa {repName}?
            </h3>
            <div className={styles.subtitulo}>
              {repName} · CC {repDoc}{repItem.unit && ` · Unidad ${etiquetaUnidad(repItem)}`} — trae poderes firmados de otros propietarios
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className={`modal-body ${styles.cuerpo}`}>
          {/* Arriba y fijo (AvisoModal): el error que dejaba al FINAL del cuerpo quedaba fuera de la vista, porque «Registrar» está en el pie fijo. */}
          {error && <AvisoModal>{error}</AvisoModal>}
          {infoErr && <div className="alert alert-danger">{infoErr}</div>}
          {!info && !infoErr && <div className="loading"><div className="spinner" /></div>}

          {info && finalizada && (
            <div className="alert alert-danger">La asamblea ya finalizó: no se pueden registrar poderes.</div>
          )}
          {info && !finalizada && (cuentaActiva ? (
            <div className={styles.cuentaActiva}>
              <i className="fas fa-check-circle" /> Cuenta activada: podrá votar por las unidades que represente.
            </div>
          ) : (
            <div className={`alert alert-warning ${styles.avisoSinMargen}`}>
              <strong>{repName}</strong> aún no ha activado su cuenta en Eventify, así que no podría votar por
              otras unidades. Reenvíale el correo de activación desde <em>Cuentas de propietarios</em>, o registra
              el poder como <em>persona externa</em> con <strong>Dar poder a…</strong> desde la fila de quien lo otorga.
            </div>
          ))}

          {info && !bloqueado && (
            <>
              <input className="form-control" value={search} aria-label="Buscar propietario"
                onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, documento o unidad" />

              <div className={styles.lista}>
                {visibles.length === 0 && (
                  <div className={styles.sinResultados}>No hay propietarios que coincidan.</div>
                )}
                {visibles.map(g => {
                  const marcado  = !!sel[g.owner.id];
                  const r        = results[g.owner.id];
                  const sinLibres = g.libres.length === 0;
                  const todasDeEste = g.filas.every(f => f.deEsteRep);
                  return (
                    <div key={g.owner.id} className={`${styles.propietario}${marcado ? ` ${styles.propietarioMarcado}` : ''}${sinLibres && !todasDeEste ? ` ${styles.propietarioApagado}` : ''}`}>
                      <label className={sinLibres ? `${styles.etiqueta} ${styles.etiquetaSinLibres}` : styles.etiqueta}>
                        <input type="checkbox" checked={marcado || todasDeEste} disabled={sinLibres}
                          onChange={() => toggleOwner(g)} className={styles.casilla} />
                        <div className={styles.texto}>
                          <div className={styles.nombre}>{nombreDe(g.owner)}</div>
                          <div className={styles.datos}>
                            CC {g.owner.numero_identificacion} · Unidad{g.rows.length > 1 ? 'es' : ''} {g.rows.map(etiquetaUnidad).join(', ')}
                            {coefTotal > 0 && ` · coef. ${coefDe(g.rows).toFixed(4)}`}
                          </div>
                          {g.filas.filter(f => f.poder).map(f => (
                            <div key={f.row.id} className={f.deEsteRep ? `${styles.poderLinea} ${styles.poderLineaPropia}` : styles.poderLinea}>
                              Unidad {etiquetaUnidad(f.row)}: {f.deEsteRep ? `ya la representa ${repName}` : `ya dio poder a ${f.poder.rep_nombre}`}
                            </div>
                          ))}
                        </div>
                      </label>

                      {marcado && (
                        <div className={styles.detalle}>
                          {g.libres.length > 1 && (
                            <div>
                              <div className={styles.unidadesTitulo}>Unidades que entrega</div>
                              <div className={styles.unidades}>
                                {g.libres.map(row => {
                                  const on = sel[g.owner.id].units.includes(row.unit_id);
                                  return (
                                    <button key={row.unit_id} type="button" onClick={() => toggleUnit(g, row.unit_id)}
                                      className={`${styles.chip} ${on ? styles.chipOn : styles.chipOff}`}>
                                      <i className={`fas ${on ? 'fa-check-square' : 'fa-square'} ${styles.chipIcono}`} />
                                      {etiquetaUnidad(row)}
                                      {coefTotal > 0 && ` · ${(parseFloat(row.unit?.coefficiente_votacion) || 0).toFixed(4)}`}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <PoderUploadBox id={`poder-${g.owner.id}`} ownerName={nombreDe(g.owner)}
                            file={sel[g.owner.id]?.file || null} onFile={f => setFile(g, f)} onError={setError} />
                          {r && (
                            <div className={r.ok ? `${styles.resultado} ${styles.resultadoOk}` : styles.resultado}>
                              <i className={`fas ${r.ok ? 'fa-check-circle' : 'fa-exclamation-circle'}`} /> {r.message}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div>
                <div className={styles.barraEncabezado}>
                  <span>Coeficiente representado por poder</span>
                  <strong className={excede ? `${styles.pct} ${styles.pctExcede}` : styles.pct}>
                    {coefTotal > 0 ? `${pct.toFixed(1)}% de ${maxPct}% permitido` : 'sin coeficientes cargados'}
                  </strong>
                </div>
                {coefTotal > 0 ? (
                  <div className={styles.barraPista}>
                    <div className={excede ? `${styles.barraRelleno} ${styles.barraExcede}` : styles.barraRelleno}
                      style={{ '--ancho': `${Math.min(100, (pct / maxPct) * 100)}%` }} />
                  </div>
                ) : (
                  <div className={styles.sinCoef}>
                    Este conjunto no tiene coeficientes: el cuórum se cuenta por unidad y el tope del {maxPct}% no aplica.
                  </div>
                )}
                {excede && (
                  <div className={styles.excedeTexto}>
                    Supera el tope de la Ley 675: desmarca algún propietario o unidad.
                  </div>
                )}
              </div>

              {elegidos.length > 0 && !excede && (
                <div className={styles.resumen}>
                  <i className={`fas fa-file-signature ${styles.resumenIcono}`} />
                  <strong>{repName}</strong> votará en nombre de{' '}
                  {elegidos.map((g, i) => (
                    <span key={g.owner.id}>{i > 0 && (i === elegidos.length - 1 ? ' y ' : ', ')}<strong>{nombreDe(g.owner)}</strong> (unidad{sel[g.owner.id].units.length > 1 ? 'es' : ''} {numeros(g)})</span>
                  ))}.
                </div>
              )}
            </>
          )}

        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className={`btn btn-primary ${styles.botonRegistrar}${botonInactivo && !saving ? ` ${styles.botonInactivo}` : ''}`} onClick={guardar} disabled={saving || botonInactivo}>
            {saving
              ? <><i className="fas fa-spinner fa-spin" /> Guardando...</>
              : <><i className="fas fa-user-check" /> Registrar {elegidos.length || ''} poder{elegidos.length === 1 ? '' : 'es'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
